import { EntityConfig, EntityRegistry, EntityCategory } from './types';
import { clientsConfig, Client } from './clients';
import { jobsConfig, Job } from './jobs';
import { candidatesConfig, Candidate } from './candidates';
import { companiesConfig, Company } from './companies';
import {
  masterCompanyCategoriesConfig,
  masterIndustriesConfig,
  masterLocationsConfig,
  masterCurrenciesConfig,
  masterProficiencyLevelsConfig,
  masterJobStatusesConfig,
} from './master';

// Entity Registry - Single source of truth for all entity configurations
export const entityRegistry: EntityRegistry = {
  // Business entities
  clients: clientsConfig,
  jobs: jobsConfig,
  candidates: candidatesConfig,
  companies: companiesConfig,
  // Master data entities
  masterCompanyCategories: masterCompanyCategoriesConfig,
  masterIndustries: masterIndustriesConfig,
  masterLocations: masterLocationsConfig,
  masterCurrencies: masterCurrenciesConfig,
  masterProficiencyLevels: masterProficiencyLevelsConfig,
  masterJobStatuses: masterJobStatusesConfig,
};

/**
 * Get entity configuration by name
 * @throws Error if entity not found in registry
 */
export function getEntityConfig<T = any>(entityName: string): EntityConfig<T> {
  const config = entityRegistry[entityName];
  if (!config) {
    throw new Error(`Entity "${entityName}" not found in registry. Available entities: ${Object.keys(entityRegistry).join(', ')}`);
  }
  return config as EntityConfig<T>;
}

/**
 * Check if an entity exists in the registry
 */
export function hasEntity(entityName: string): boolean {
  return entityName in entityRegistry;
}

/**
 * Get all registered entity names
 */
export function getEntityNames(): string[] {
  return Object.keys(entityRegistry);
}

/**
 * Get entity names by category
 */
export function getEntityNamesByCategory(category: EntityCategory): string[] {
  return Object.entries(entityRegistry)
    .filter(([_, config]) => config.category === category)
    .map(([name]) => name);
}

/**
 * Get all entity configurations
 */
export function getAllEntityConfigs(): EntityConfig[] {
  return Object.values(entityRegistry);
}

/**
 * Get entities grouped by category
 */
export function getEntitiesGroupedByCategory(): Record<EntityCategory, Array<{ name: string; config: EntityConfig }>> {
  const grouped: Record<EntityCategory, Array<{ name: string; config: EntityConfig }>> = {
    business: [],
    master: [],
    system: [],
  };

  Object.entries(entityRegistry).forEach(([name, config]) => {
    const category = config.category || 'business';
    grouped[category].push({ name, config });
  });

  return grouped;
}

// Re-export types and entity configs
export * from './types';
export { clientsConfig, type Client };
export { jobsConfig, type Job };
export { candidatesConfig, type Candidate };
export { companiesConfig, type Company };
export * from './master';
